import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import { SuiContainer, SuiLabel } from '@splashthat/sui';
import FontPickerEditor from 'common/components/FontPicker';
import ColorPickerEditor from 'common/components/ColorPicker';
import AlignmentEditor from 'common/components/AlignmentEditor';
import { getColors } from 'common/entities/color/selectors';
import { getFonts } from 'common/entities/font/selectors';
import {
    getHeadLineTextStyles,
    getHeadLineTextPosition,
    getFormHeaderImageStyles,
} from 'common/entities/form/styleConfiguration/selectors';
import {
    setFormHeadLineTextFont,
    setFormHeadLineTextFontsize,
    setFormHeadLineTextColor,
    setFormHeadlinePosition,
    setFormHeaderImagePosition,
} from 'common/entities/form/styleConfiguration/actions';
import {
    DEFAULT_FONT,
    DEFAULT_HEADLINE_SIZE,
    DEFAULT_HEADLINE_COLOR,
    DEFAULT_HEADLINE_POSITION,
    DEFAULT_LOGO_POSITION,
} from 'common/entities/form/styleConfiguration/defaultValues';
import { LEFT, CENTER, RIGHT, LOGO_TYPE } from 'common/entities/form/styleConfiguration/constants';
import { getDefaultStyle, styleShapePropTypes } from 'common/entities/form/styleConfiguration/utils';
import { getFormId } from '../../../selectors';

import {
    HEADLINE_FONT_LABEL,
    HEADLINE_LOGO_LABEL,
    HEADLINE_ALIGNMENT_LABEL,
    HEADLINE_IMAGE_LABEL,
} from '../../../constants/formStyleLabels';
import { COLLAPSIBLE_CONTENT, COLLAPSIBLE_SECTION } from '../../../constants/reusableClassNames';
import FormImageManager from '../../FormImageManager';

import { HEADLINE_TEXT_QA, FONT_LABEL_QA } from '../../../utils/qaIdConstants';

const baseClassName = 'FormStyleEditorHeadlinePanel';

const FormStyleEditorHeadlinePanel = ({ formId, colorList, fontList, ...props }) => (
    <SuiContainer className={COLLAPSIBLE_CONTENT} data-qa={`${HEADLINE_TEXT_QA}--panel`}>
        <SuiContainer className={COLLAPSIBLE_SECTION} data-qa={`${HEADLINE_TEXT_QA}--section-${FONT_LABEL_QA}`}>
            <SuiLabel text={HEADLINE_FONT_LABEL} data-qa={`${HEADLINE_TEXT_QA}--label`} />
            <FontPickerEditor
                fontFamilyList={fontList}
                defaultFontFamily={props.font}
                onChangeFontFamily={(id) => props.setFormHeadLineTextFont(formId, id)}
                defaultFontSize={props.fontSize}
                onChangeFontSize={(size) => props.setFormHeadLineTextFontsize(formId, size)}
                fontFamilyQa={`${HEADLINE_TEXT_QA}--fontFamily`}
                fontSizeQa={`${HEADLINE_TEXT_QA}--fontSize`}
            />
            <ColorPickerEditor
                colorList={colorList}
                firstColor={props.color}
                changeFirstColor={(id) => props.setFormHeadLineTextColor(formId, id)}
                qaId={HEADLINE_TEXT_QA}
            />
        </SuiContainer>
        <SuiContainer className={COLLAPSIBLE_SECTION} data-qa={`${HEADLINE_TEXT_QA}--section-alignment`}>
            <SuiContainer className={`${baseClassName}__AlignmentEditor`}>
                <AlignmentEditor
                    label={HEADLINE_ALIGNMENT_LABEL}
                    position={props.position}
                    onChangePosition={(position) => props.setFormHeadlinePosition(formId, position)}
                    qaId={`${HEADLINE_TEXT_QA}--alignment`}
                />
            </SuiContainer>
        </SuiContainer>
        <SuiContainer className={COLLAPSIBLE_SECTION} data-qa={`${HEADLINE_TEXT_QA}--section-edit-image`}>
            <FormImageManager label={HEADLINE_IMAGE_LABEL} />
        </SuiContainer>
        {props.hasLogo && (
            <SuiContainer
                className={COLLAPSIBLE_SECTION}
                data-qa={`${HEADLINE_TEXT_QA}--section-${HEADLINE_LOGO_LABEL}`}
            >
                <SuiLabel text={HEADLINE_LOGO_LABEL} data-qa={`${HEADLINE_TEXT_QA}--label`} />
                <AlignmentEditor
                    position={props.logoPosition}
                    onChangePosition={(position) => props.setFormHeaderImagePosition(formId, position)}
                    qaId={`${HEADLINE_TEXT_QA}--alignment`}
                />
            </SuiContainer>
        )}
    </SuiContainer>
);

FormStyleEditorHeadlinePanel.propTypes = {
    colorList: PropTypes.arrayOf(PropTypes.object).isRequired,
    fontList: PropTypes.arrayOf(PropTypes.object).isRequired,
    formId: PropTypes.string.isRequired,
    color: styleShapePropTypes,
    font: styleShapePropTypes,
    fontSize: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    position: PropTypes.oneOf([LEFT, CENTER, RIGHT]).isRequired,
    hasLogo: PropTypes.bool,
    logoPosition: PropTypes.oneOf([LEFT, CENTER, RIGHT]).isRequired,
    setFormHeadLineTextFont: PropTypes.func.isRequired,
    setFormHeadLineTextFontsize: PropTypes.func.isRequired,
    setFormHeadLineTextColor: PropTypes.func.isRequired,
    setFormHeadlinePosition: PropTypes.func.isRequired,
    setFormHeaderImagePosition: PropTypes.func.isRequired,
};

FormStyleEditorHeadlinePanel.defaultProps = {
    color: getDefaultStyle(DEFAULT_HEADLINE_COLOR),
    font: getDefaultStyle(DEFAULT_FONT),
    fontSize: DEFAULT_HEADLINE_SIZE,
    hasLogo: false,
};

const mapStateToProps = (state, props) => {
    const formId = getFormId(state);
    const colorList = getColors(state);
    const fontList = getFonts(state);

    const headlineTextStyles = getHeadLineTextStyles(state, formId);
    const headlineTextPosition = getHeadLineTextPosition(state, formId) || DEFAULT_HEADLINE_POSITION;
    const { url, type, position } = getFormHeaderImageStyles(state, formId);

    return {
        colorList,
        fontList,
        formId,
        position: headlineTextPosition,
        hasLogo: !!url && type === LOGO_TYPE,
        logoPosition: position || DEFAULT_LOGO_POSITION,
        ...headlineTextStyles,
    };
};

const mapDispatchToProps = {
    setFormHeadLineTextFont,
    setFormHeadLineTextFontsize,
    setFormHeadLineTextColor,
    setFormHeadlinePosition,
    setFormHeaderImagePosition,
};

export default connect(mapStateToProps, mapDispatchToProps)(FormStyleEditorHeadlinePanel);
